import { addMonths, endOfDay, format } from 'date-fns'
import React, { type FunctionComponent, useState } from 'react'
import { Dialog } from '../../../common/Dialog'
import { API_HOST, authenticatedFetchData } from '../../../../lib'
import { Button } from '@tomra/design-system-react/button'

type Props = {
  campaign: CharityCampaignType
  onExtended: (newEndDate: Date) => void
}

export const ExtendCampaignDialog: FunctionComponent<Props> = ({ campaign, onExtended }) => {
  const [showExtendDialog, setShowExtendDialog] = useState(false)
  const [endDate, setEndDate] = useState(format(new Date(campaign.endTime), 'yyyy-MM-dd'))
  const [requestStatus, setRequestStatus] = useState<RequestStatusType>('idle')
  const [error, setError] = useState('')

  function addMonthsToEndDate(numMonths: number) {
    return () => {
      const newEndDate = addMonths(new Date(campaign.endTime), numMonths)
      setEndDate(format(newEndDate, 'yyyy-MM-dd'))
    }
  }

  function _onExtend() {
    setError('')
    setRequestStatus('pending')

    const newEndDate = endOfDay(new Date(endDate))

    authenticatedFetchData(
      `${API_HOST}/charities/admin/v1/charities/${campaign.charityId}/campaign/${campaign.campaignId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ ...campaign, endTime: newEndDate.toISOString() })
      }
    )
      .run()
      .then(() => {
        setRequestStatus('idle')
        onExtended(newEndDate)
        setShowExtendDialog(false)
      })
      .catch(error => {
        const errorMessage = error.body?.detail || 'Failed to extend campaign'

        setRequestStatus('failure')
        setError(errorMessage)
      })
  }

  return (
    <>
      <div className="flex justify-end mb-md">
        <Button onClick={() => setShowExtendDialog(true)}>Extend</Button>
      </div>

      {showExtendDialog && (
        <Dialog ariaLabel={`Extend campaign ${campaign.name}`} onClose={() => setShowExtendDialog(false)}>
          <div className="card min-w-screen-sm max-w-screen-sm grid gap-md">
            <div className="p-lg text-center grid gap-lg">
              <h2 className="text-xl">Extend campaign</h2>

              <div className="flex items-center justify-center space-x-md">
                <button className="tag" onClick={addMonthsToEndDate(3)}>
                  3 months
                </button>
                <button className="tag" onClick={addMonthsToEndDate(6)}>
                  6 months
                </button>
                <button className="tag" onClick={addMonthsToEndDate(12)}>
                  12 months
                </button>
              </div>

              <div className="flex flex-col items-start justify-center px-[5rem]">
                <label htmlFor="newDate" className="label">
                  End date
                </label>
                <input
                  id="newDate"
                  type="date"
                  value={format(new Date(endDate), 'yyyy-MM-dd')}
                  onChange={({ target }) => {
                    setEndDate(target.value)
                  }}
                  className="border border-gray-300 rounded px-3 py-2 bg-white w-full focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent"
                />
              </div>

              {error && <div className="alert alert-danger">{error}</div>}
            </div>

            <div className="flex space-x-md justify-end p-lg bg-grey-light rounded-b-half">
              <Button disabled={requestStatus === 'pending'} onClick={() => setShowExtendDialog(false)}>
                Close
              </Button>
              <Button variant="primary" disabled={requestStatus === 'pending'} onClick={_onExtend}>
                {requestStatus === 'pending' ? <span className="loadingSpinner" /> : 'Extend'}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  )
}
